// src/utils/navigationHelpers.js
// Navigation utilities extracted from UnitViewer.js

/**
 * Get all content sorted by code (creates the main navigation sequence)
 */
export const getAllContentByCode = (units, packages) => {
    const allContent = [...units, ...packages];

    // Group by code first
    const contentByCode = allContent.reduce((acc, item) => {
        const code = item.code || '';
        if (!acc[code]) {
            acc[code] = [];
        }
        acc[code].push(item);
        return acc;
    }, {});

    // Get all items and sort by their order field (NOT by code alphabetically)
    const sortedContent = Object.keys(contentByCode).map(code => {
        const items = contentByCode[code];

        // Filter out items that don't have actual content
        const validItems = items.filter(item => {
            // Check if item has meaningful content
            if (item.type === 'package' && (!item.unitIds || item.unitIds.length === 0)) {
                return false; // Skip packages with no units
            }
            if (item.type === 'story' && !item.pages && !item.content) {
                return false; // Skip stories with no content
            }
            if (item.content === undefined && !item.pages) {
                return false; // Skip items with no content at all
            }
            return true;
        });

        if (validItems.length === 0) {
            return null; // Skip this code entirely if no valid content
        }

        // For packages, prefer the package item over individual units
        const packageItem = validItems.find(item => item.type === 'package');
        const selectedItem = packageItem || validItems[0]; // Return package if exists, otherwise first valid item
        
        return selectedItem;
    })
    .filter(item => item !== null) // Remove null entries
    .sort((a, b) => {
        // Sort by order field
        const orderA = a.order || 0;
        const orderB = b.order || 0;
        
        if (orderA !== orderB) {
            return orderA - orderB;
        }
        
        // If orders are the same, fall back to code comparison
        const codeA = a.code || '';
        const codeB = b.code || '';
        return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
    });

    return sortedContent;
};

/**
 * Create touch event handlers for mobile navigation
 */
export const createTouchHandlers = (onNext, onPrev) => {
    let touchStartX = null;

    const handleTouchStart = (e) => {
        touchStartX = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
        if (!touchStartX) return;

        const touchEndX = e.touches[0].clientX;
        const diff = touchStartX - touchEndX;

        // Prevent scrolling when swiping horizontally
        if (Math.abs(diff) > 10) {
            e.preventDefault();
        }
    };

    const handleTouchEnd = (e) => {
        if (!touchStartX) return;

        const touchEndX = e.changedTouches[0].clientX;
        const diff = touchStartX - touchEndX;

        // Swipe threshold
        if (Math.abs(diff) > 50) {
            if (diff > 0) {
                // Swipe left (next unit)
                onNext();
            } else {
                // Swipe right (previous unit)
                onPrev();
            }
        }

        touchStartX = null;
    };

    return {
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd
    };
};

/**
 * Calculate navigation bounds and availability
 */
export const getNavigationState = (currentIndex, internalIndex, totalUnits, internalTotal) => {
    return {
        canGoPrev: currentIndex > 0,
        canGoNext: currentIndex < totalUnits - 1,
        canGoInternalPrev: internalIndex > 0,
        canGoInternalNext: internalIndex < internalTotal - 1,
        hasInternalNavigation: internalTotal > 1,
        isFirstUnit: currentIndex === 0,
        isLastUnit: currentIndex === totalUnits - 1,
        isFirstInternalPage: internalIndex === 0,
        isLastInternalPage: internalIndex === internalTotal - 1
    };
};

/**
 * Determine if internal pagination should be shown
 */
export const shouldShowInternalPagination = (internalContent) => {
    return internalContent && internalContent.length > 1;
};

/**
 * Create progress indicators for internal pages
 */
export const createInternalProgressInfo = (internalContent, currentInternalIndex, visitedPages = new Set()) => {
    if (!internalContent || internalContent.length <= 1) {
        return null;
    }

    return internalContent.map((item, index) => {
        const pageId = item.id || `page_${index}`;
        return {
            index,
            id: pageId,
            isActive: index === currentInternalIndex,
            isVisited: visitedPages.has(pageId),
            title: item.title || `Page ${index + 1}`
        };
    });
};

/**
 * Calculate overall progress percentage
 */
export const calculateProgress = (currentIndex, totalUnits, internalIndex = 0, internalTotal = 1) => {
    if (totalUnits === 0) return 0;
    
    // Base progress from completed units
    const baseProgress = currentIndex / totalUnits;
    
    // Add progress from current unit's internal pages
    const internalProgress = internalTotal > 1 ? (internalIndex / internalTotal) / totalUnits : 0;
    
    return Math.min(100, (baseProgress + internalProgress) * 100);
};

/**
 * Generate unique identifiers for content tracking
 */
export const createContentId = (contentId, pageIndex = null) => {
    if (pageIndex !== null && pageIndex >= 0) {
        return `${contentId}_page_${pageIndex}`;
    }
    return contentId;
};

/**
 * Check if content should be auto-completed based on type
 */
export const shouldAutoComplete = (contentType) => {
    return ['story', 'discussion', 'Assignment', 'interactive-image'].includes(contentType);
};

/**
 * Get the current page index from internal content
 */
export const getCurrentPageIndex = (currentItem) => {
    if (!currentItem || !currentItem.id) return 0;
    
    const pageMatch = currentItem.id.match(/_page_(\d+)$/);
    return pageMatch ? parseInt(pageMatch[1], 10) : 0;
};